import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  Card,
  IconButton,
  Chip,
  Grid
} from '@mui/joy';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PhotoIcon from '@mui/icons-material/Photo';
import { FileUploadService } from '../utils/fileUploadService';
import { getFileType, formatFileSize } from '../utils/fileUploadConfig';

const FileUpload = ({ 
  surveyProjectId, 
  userId, 
  onFilesUploaded, 
  acceptedTypes = 'documents,photos',
  disabled = false 
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState('');
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const acceptedTypesArray = acceptedTypes.split(',');
  
  const getAcceptAttribute = () => {
    const types = [];
    if (acceptedTypesArray.includes('documents')) {
      types.push('.pdf,.doc,.docx,.xls,.xlsx,.txt');
    }
    if (acceptedTypesArray.includes('photos')) {
      types.push('.jpg,.jpeg,.png,.heic,.webp');
    }
    return types.join(',');
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    if (!disabled) {
      setDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const validateFiles = (files) => {
    const validFiles = [];
    const errors = [];

    Array.from(files).forEach(file => {
      try {
        const fileType = getFileType(file);
        if (!fileType) {
          errors.push(`${file.name}: Unsupported file type`);
          return;
        }
        
        if (!acceptedTypesArray.includes(fileType)) {
          errors.push(`${file.name}: File type not allowed for this upload`);
          return;
        }

        validFiles.push(file);
      } catch (err) {
        errors.push(`${file.name}: ${err.message}`);
      }
    });

    return { validFiles, errors };
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;

    const files = e.dataTransfer.files;
    const { validFiles, errors } = validateFiles(files);
    
    if (errors.length > 0) {
      setError(errors.join(', '));
      setTimeout(() => setError(''), 5000);
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  }, [disabled, acceptedTypesArray]);

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (!files || disabled) return;

    const { validFiles, errors } = validateFiles(files);
    
    if (errors.length > 0) {
      setError(errors.join(', '));
      setTimeout(() => setError(''), 5000);
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }

    // Reset input
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0 || !surveyProjectId || !userId) return;

    setUploading(true);
    setError('');
    setUploadProgress(0);

    try {
      const results = await FileUploadService.uploadMultipleFiles(
        selectedFiles,
        surveyProjectId,
        userId,
        (current, total, fileName) => {
          setCurrentFile(fileName);
          setUploadProgress((current / total) * 100);
        }
      );

      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      if (failed.length > 0) {
        const errorMessages = failed.map(f => `${f.fileName}: ${f.error}`);
        setError(errorMessages.join(', '));
      }

      if (successful.length > 0) {
        setSelectedFiles([]);
        if (onFilesUploaded) {
          onFilesUploaded(successful);
        }
      }

    } catch (err) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setCurrentFile('');
    }
  };

  const getFileIcon = (file) => {
    const fileType = getFileType(file);
    return fileType === 'photos' ? <PhotoIcon /> : <InsertDriveFileIcon />;
  };

  const getFileTypeChip = (file) => {
    const fileType = getFileType(file);
    return (
      <Chip 
        size="sm" 
        variant="soft" 
        color={fileType === 'photos' ? 'primary' : 'neutral'}
      >
        {fileType === 'photos' ? 'Photo' : 'Document'}
      </Chip>
    );
  };

  return (
    <Box>
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Upload Area */}
      <Card
        variant="outlined"
        sx={{
          p: 3,
          border: dragOver ? '2px dashed' : '2px dashed transparent',
          borderColor: dragOver ? 'success.500' : 'neutral.300',
          backgroundColor: dragOver ? 'success.50' : 'background.body',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          transition: 'all 0.2s ease',
          '&:hover': disabled ? {} : {
            borderColor: 'success.400',
            backgroundColor: 'success.25'
          }
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CloudUploadIcon sx={{ fontSize: 48, color: 'success.500', mb: 1 }} />
          <Typography level="h4" sx={{ mb: 1 }}>
            {acceptedTypesArray.includes('photos') && acceptedTypesArray.includes('documents') 
              ? 'Upload Documents & Photos'
              : acceptedTypesArray.includes('photos') 
                ? 'Upload Photos' 
                : 'Upload Documents'
            }
          </Typography>
          <Typography level="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Drag and drop files here or click to browse
          </Typography>
          <Typography level="body3" sx={{ color: 'text.tertiary' }}>
            {acceptedTypesArray.includes('documents') && 
              'Documents: PDF, DOC, DOCX, XLS, XLSX, TXT (max 50MB) → survey-documents bucket'
            }
            {acceptedTypesArray.includes('documents') && acceptedTypesArray.includes('photos') && 
              ' • '
            }
            {acceptedTypesArray.includes('photos') && 
              'Photos: JPG, PNG, HEIC, WEBP (max 25MB) → survey-photos bucket'
            }
          </Typography>
        </Box>
      </Card>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={getAcceptAttribute()}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        disabled={disabled}
      />

      {/* Selected Files */}
      {selectedFiles.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography level="title-sm" sx={{ mb: 1 }}>
            Selected Files ({selectedFiles.length})
          </Typography>
          <Grid container spacing={1}>
            {selectedFiles.map((file, index) => (
              <Grid xs={12} key={index}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getFileIcon(file)}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography level="body2" noWrap>
                        {file.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                        {getFileTypeChip(file)}
                        <Typography level="body3" sx={{ color: 'text.secondary' }}>
                          {formatFileSize(file.size)} • {getFileType(file) === 'photos' ? 'survey-photos' : 'survey-documents'}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="sm"
                      color="danger"
                      onClick={() => removeFile(index)}
                      disabled={uploading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Upload Button */}
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button
              onClick={uploadFiles}
              disabled={uploading || selectedFiles.length === 0}
              loading={uploading}
              startDecorator={<CloudUploadIcon />}
              sx={{ flexGrow: 1 }}
            >
              {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setSelectedFiles([])}
              disabled={uploading}
            >
              Clear
            </Button>
          </Box>

          {/* Upload Progress */}
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography level="body3">
                  Uploading: {currentFile}
                </Typography>
                <Typography level="body3">
                  {Math.round(uploadProgress)}%
                </Typography>
              </Box>
              <LinearProgress 
                determinate 
                value={uploadProgress}
                sx={{
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'success.500'
                  }
                }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default FileUpload;